import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../../contexts/AuthContext';
import {
  STATUS_CHECKLIST, STATUS_EDITAVEIS, STATUS_RETORNO, TIPO_ATENDIMENTO_LABEL,
  conferirQuantidades, fmtQtd, lerQtd,
} from '../../helpers/insumoChecklist';
import { formatDateBR } from '../../helpers/formatDate';

// Bloco "Checklist de insumos" dentro do detalhe do chamado.
//
//   - lista os checklists vinculados ao chamado;
//   - "Abrir checklist" leva para a aba Insumos com um checklist novo ja
//     vinculado a este chamado;
//   - encerra o checklist daqui mesmo, com o chamado continuando aberto:
//       antes da saida  -> cancela (nada saiu do estoque)
//       depois da saida -> informa usado/devolvido e finaliza
//   - cada checklist em aberto tem a opcao "encerrar junto ao fechar o
//     chamado". A tela de Chamados chama `encerrarMarcados()` (via ref) antes
//     de resolver/fechar/cancelar o chamado; desmarcado, o chamado fecha e o
//     checklist segue aberto.
//
// As conferencias (retirada = usada + devolvida) sao as mesmas da aba Insumos;
// o backend valida de novo.

const aberto = (c) => STATUS_EDITAVEIS.includes(c.status) || STATUS_RETORNO.includes(c.status);

function StatusPill({ status }) {
  const s = STATUS_CHECKLIST[status] || { label: status, pill: '' };
  return <span className={`status-pill ${s.pill}`}>{s.label}</span>;
}

const ChecklistsDoChamado = forwardRef(function ChecklistsDoChamado({ chamado, chamadoAberto, onMudou }, ref) {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);
  // { [checklistId]: { [itemId]: { qtd_usada, qtd_devolvida } } }
  const [retornos, setRetornos] = useState({});
  // { [checklistId]: boolean } — encerrar junto ao fechar o chamado
  const [junto, setJunto] = useState({});

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/insumos/checklists`, { params: { chamado_id: chamado.id } });
      const lista = res.data.data || [];
      setChecklists(lista);
      setRetornos((atual) => {
        const novo = { ...atual };
        lista.filter((c) => STATUS_RETORNO.includes(c.status)).forEach((c) => {
          if (novo[c.id]) return;
          novo[c.id] = Object.fromEntries(c.itens.map((it) => [it.id, {
            qtd_usada: it.qtd_usada != null ? String(Number(it.qtd_usada)) : '',
            qtd_devolvida: it.qtd_devolvida != null ? String(Number(it.qtd_devolvida)) : '',
          }]));
        });
        return novo;
      });
      setJunto((atual) => {
        const novo = { ...atual };
        lista.filter(aberto).forEach((c) => { if (novo[c.id] === undefined) novo[c.id] = true; });
        return novo;
      });
      setErro('');
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível carregar os checklists deste chamado.');
    } finally {
      setCarregando(false);
    }
  }, [chamado.id]);

  useEffect(() => { carregar(); }, [carregar]);

  const conferencias = (c) => c.itens.map((it) => ({
    it,
    conf: conferirQuantidades({
      nome: it.nome,
      qtd_retirada: it.qtd_retirada,
      qtd_usada: retornos[c.id]?.[it.id]?.qtd_usada,
      qtd_devolvida: retornos[c.id]?.[it.id]?.qtd_devolvida,
    }),
  }));

  // Encerra UM checklist. Devolve mensagem de erro ou null.
  const encerrar = async (c) => {
    if (STATUS_EDITAVEIS.includes(c.status)) {
      try {
        await axios.post(`${API}/insumos/checklists/${c.id}/cancelar`);
        return null;
      } catch (err) {
        return err.response?.data?.message || `Falha ao cancelar o checklist #${c.id}.`;
      }
    }
    const pendentes = conferencias(c).filter(({ conf }) => !conf.ok);
    if (pendentes.length) {
      return `Checklist #${c.id}: ${pendentes[0].conf.mensagem}`;
    }
    try {
      await axios.post(`${API}/insumos/checklists/${c.id}/finalizar`, {
        itens: c.itens.map((it) => ({
          id: it.id,
          qtd_usada: lerQtd(retornos[c.id]?.[it.id]?.qtd_usada),
          qtd_devolvida: lerQtd(retornos[c.id]?.[it.id]?.qtd_devolvida),
        })),
      });
      return null;
    } catch (err) {
      return err.response?.data?.message || `Falha ao finalizar o checklist #${c.id}.`;
    }
  };

  // Usado pela tela de Chamados antes de resolver/fechar/cancelar o chamado.
  useImperativeHandle(ref, () => ({
    async encerrarMarcados() {
      const alvos = checklists.filter((c) => aberto(c) && junto[c.id]);
      // Confere tudo antes de encerrar o primeiro, para nao parar no meio.
      for (const c of alvos) {
        if (STATUS_RETORNO.includes(c.status)) {
          const pendente = conferencias(c).find(({ conf }) => !conf.ok);
          if (pendente) {
            return `Checklist #${c.id}: ${pendente.conf.mensagem} `
              + 'Preencha o usado e o devolvido, ou desmarque "encerrar junto" para fechar só o chamado.';
          }
        }
      }
      for (const c of alvos) {
        const msg = await encerrar(c);
        if (msg) { await carregar(); return msg; }
      }
      if (alvos.length) await carregar();
      return null;
    },
  }));

  const encerrarAgora = async (c) => {
    setSalvandoId(c.id);
    setErro('');
    const msg = await encerrar(c);
    if (msg) setErro(msg);
    await carregar();
    setSalvandoId(null);
    if (!msg) onMudou?.();
  };

  const setRet = (cid, itemId, patch) => setRetornos((r) => ({
    ...r, [cid]: { ...r[cid], [itemId]: { ...r[cid]?.[itemId], ...patch } },
  }));

  return (
    <div className="chamado-ck">
      <div className="chamado-ck__topo">
        <span className="chamado-ck__titulo"><i className="bi bi-clipboard-check me-1" />Checklist de insumos</span>
        {/* So chamado em aberto recebe checklist novo (o backend recusa o vinculo a chamado encerrado). */}
        {chamadoAberto ? (
          <button
            type="button" className="btn btn-sm"
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
            onClick={() => navigate(`/ti/insumos?novo=1&chamado=${chamado.id}`)}
          >
            <i className="bi bi-plus-lg me-1" />Abrir checklist
          </button>
        ) : null}
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem', whiteSpace: 'pre-line' }}>{erro}</div> : null}

      {carregando ? (
        <div className="cell-muted" style={{ fontSize: '0.82rem' }}>Carregando...</div>
      ) : !checklists.length ? (
        <div className="cell-muted" style={{ fontSize: '0.82rem' }}>
          {chamadoAberto
            ? 'Nenhum checklist vinculado. Use "Abrir checklist" para separar materiais e ferramentas deste chamado.'
            : 'Nenhum checklist foi vinculado a este chamado.'}
        </div>
      ) : checklists.map((c) => {
        const emRetorno = STATUS_RETORNO.includes(c.status);
        const preSaida = STATUS_EDITAVEIS.includes(c.status);
        const confs = emRetorno ? conferencias(c) : [];
        const tudoConfere = confs.length > 0 && confs.every(({ conf }) => conf.ok);
        const salvando = salvandoId === c.id;
        return (
          <div key={c.id} className="chamado-ck__item">
            <div className="chamado-ck__linha">
              <span>
                <strong>#{c.id}</strong> · {TIPO_ATENDIMENTO_LABEL[c.tipo_atendimento]}
                {c.localidade ? ` · ${c.localidade}` : ''}
                {c.data_atendimento ? ` · ${formatDateBR(c.data_atendimento)}` : ''}
              </span>
              <StatusPill status={c.status} />
              <button
                type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => navigate(`/ti/insumos?checklist=${c.id}`)}
                title="Abrir este checklist na aba Insumos"
              >
                <i className="bi bi-box-arrow-up-right me-1" />Abrir
              </button>
            </div>

            {preSaida ? (
              <div className="chamado-ck__nota">
                {c.itens.length
                  ? `Materiais previstos: ${c.itens.map((it) => `${it.nome} (${fmtQtd(it.qtd_retirada)})`).join(', ')}.`
                  : 'Ainda sem materiais.'}
                {' '}Nada saiu do estoque — para conferir e confirmar a saída, use "Abrir".
              </div>
            ) : null}

            {emRetorno ? (
              <div className="table-shell">
                <table className="portal-table chamado-ck__tabela">
                  <thead><tr><th>Material</th><th>Retirada</th><th>Usada</th><th>Devolvida</th><th /></tr></thead>
                  <tbody>
                    {confs.map(({ it, conf }) => (
                      <tr key={it.id}>
                        <td>{it.nome}</td>
                        <td>{fmtQtd(it.qtd_retirada)}</td>
                        <td>
                          <input
                            className="insumo-ck__qtd" inputMode="decimal" disabled={salvando}
                            value={retornos[c.id]?.[it.id]?.qtd_usada ?? ''}
                            onChange={(e) => setRet(c.id, it.id, { qtd_usada: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="insumo-ck__qtd" inputMode="decimal" disabled={salvando}
                            value={retornos[c.id]?.[it.id]?.qtd_devolvida ?? ''}
                            onChange={(e) => setRet(c.id, it.id, { qtd_devolvida: e.target.value })}
                          />
                        </td>
                        <td>
                          {conf.ok
                            ? <span className="insumo-ck__ok" title="Quantidades conferem"><i className="bi bi-check-circle-fill" /></span>
                            : conf.pendente
                              ? <span className="cell-muted">preencher</span>
                              : <span className="insumo-ck__alerta" title={conf.mensagem}><i className="bi bi-exclamation-triangle-fill" /> não confere</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {!aberto(c) && c.itens.length ? (
              <div className="chamado-ck__nota">
                {c.status === 'finalizado'
                  ? c.itens.map((it) => `${it.nome}: usado ${fmtQtd(it.qtd_usada)}, devolvido ${fmtQtd(it.qtd_devolvida)}`).join(' · ')
                  : 'Cancelado antes da saída.'}
              </div>
            ) : null}

            {aberto(c) ? (
              <div className="chamado-ck__acoes">
                {chamadoAberto ? (
                  <label title="Desmarque para fechar o chamado e deixar o checklist em aberto">
                    <input
                      type="checkbox" checked={!!junto[c.id]}
                      onChange={(e) => setJunto((j) => ({ ...j, [c.id]: e.target.checked }))}
                    />
                    {preSaida ? 'Cancelar este checklist' : 'Finalizar este checklist'} junto ao resolver/fechar o chamado
                  </label>
                ) : <span />}
                <button
                  type="button" className="btn btn-sm btn-outline-secondary"
                  disabled={salvando || (emRetorno && !tudoConfere)}
                  onClick={() => encerrarAgora(c)}
                  title={emRetorno && !tudoConfere ? 'Informe usado e devolvido de todos os materiais' : 'O chamado continua aberto'}
                >
                  {salvando ? 'Encerrando...' : preSaida ? 'Cancelar checklist agora' : 'Finalizar checklist agora'}
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
});

export default ChecklistsDoChamado;
